import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Search, ChevronLeft, ChevronRight, Loader2, Check, Minus } from 'lucide-react';
import { 
  SharePointItemsViewProps, 
  SharePointListItem
} from '../../types/sharepoint.types';
import TreeView from './TreeView';
import "../../styles/loader.css";
import { getLibrarySelectionStateFromExclusions } from './sharepointHelpers';

const SharePointItemsView: React.FC<SharePointItemsViewProps> = ({
  items,
  isLoading,
  error,
  selectedItem,
  itemType,
  onBack,
  pagination,
  isLoadingPage,
  onNavigateNext,
  onNavigatePrevious,
  onPageSizeChange,
  onSearch,
  appliedFilters,
  onDocumentExclusion,
  onLibraryExclusion,  
  isDocumentExcluded,
  sharepointId,
  sharepoint 
}) => {
  const [localSearchTerm, setLocalSearchTerm] = useState("");
  
  const pageSizeOptions = [10, 25, 50, 100];
  const isDocumentLibraries = itemType === 'document_libraries';
  const useBackendSearch = typeof onSearch === 'function';

 const getLibrarySelectionState = (): 'checked' | 'unchecked' | 'indeterminate' => {
  if (!isDocumentLibraries || !selectedItem || !sharepoint) {
    return 'unchecked';
  }
  
  const libraryId = selectedItem.library_id || selectedItem.drive_id || selectedItem.id;
  if (!libraryId) return 'unchecked';
  
  return getLibrarySelectionStateFromExclusions(libraryId, sharepoint.excludedItems);
};

  const countAllDocuments = (items: SharePointListItem[]): number => {
    let count = 0;
    items.forEach(item => {
      if (!item.is_folder) {
        count++;
      } else if (item.children) {
        count += countAllDocuments(item.children as SharePointListItem[]);
      }
    });
    return count;
  };

  const getAllDocumentIds = (items: SharePointListItem[]): string[] => {
    const ids: string[] = [];
    items.forEach(item => {
      if (!item.is_folder) {
        ids.push(item.id);
      } else if (item.children) {
        ids.push(...getAllDocumentIds(item.children as SharePointListItem[]));
      }
    });
    return ids;
  };

const handleLibraryHeaderCheckboxClick = () => {
  if (!isDocumentLibraries || !onLibraryExclusion || !selectedItem) {
    return;
  }

  const libraryId = selectedItem.library_id || selectedItem.drive_id || selectedItem.id;
  
  if (!libraryId) {
    return;
  }

  const currentState = getLibrarySelectionState();
  
  if (currentState === 'checked') {
    onLibraryExclusion(sharepointId, libraryId, true);
  } else if (currentState === 'unchecked') {
    onLibraryExclusion(sharepointId, libraryId, false);
  } else if (currentState === 'indeterminate') {
    onLibraryExclusion(sharepointId, libraryId, false);
  } else {
    onLibraryExclusion(sharepointId, libraryId, false);
  }
};

  const availableFields = useMemo(() => {
    if (isDocumentLibraries || !items || items.length === 0) return [];
    
    const fieldSet = new Set<string>();
    
    items.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'fields') {
          fieldSet.add(key);
        }
      });
      
      if (item.fields && typeof item.fields === 'object') {
        Object.keys(item.fields).forEach(key => {
          fieldSet.add(key);
        });
      }
    });
    
    const filteredFields = Array.from(fieldSet).filter(field => {
      const lowerField = field.toLowerCase();
      
      if (field.startsWith('@') || field.startsWith('_')) return false;
      
      const systemPatterns = [
        'odata', 'etag', 'version', 'compliance', 'lookup', 'attachments',
        'edit', 'childcount', 'contenttype', 'uiversion', 'author', 'editor'
      ];
      
      if (systemPatterns.some(pattern => lowerField.includes(pattern))) return false;
      if (field.endsWith('Id') && field !== 'id') return false;
      
      return true;
    });
    
    const priorityFields = ['id', 'title', 'name', 'projectid', 'projectname', 'status', 'created', 'modified'];
    
    return filteredFields.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();
      
      const aIndex = priorityFields.findIndex(p => aLower.includes(p));
      const bIndex = priorityFields.findIndex(p => bLower.includes(p));
      
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return a.localeCompare(b);
    });
  }, [items, isDocumentLibraries]);

  const getFieldValue = (item: SharePointListItem, fieldName: string): any => {
    if (item[fieldName] !== undefined) {
      return item[fieldName];
    }
    
    if (item.fields && typeof item.fields === 'object' && item.fields[fieldName] !== undefined) {
      return item.fields[fieldName];
    }
    
    return null;
  };

  const formatFieldValue = (value: any, fieldName: string): string => {
    if (value === null || value === undefined) return 'N/A';
    
    const lowerFieldName = fieldName.toLowerCase();
    
    if (lowerFieldName.includes('complete') || lowerFieldName.includes('percent')) {
      if (typeof value === 'number') {
        return `${(value * 100).toFixed(1)}%`;
      }
    }
    
    if (lowerFieldName.includes('budget') || lowerFieldName.includes('cost') || lowerFieldName.includes('amount')) {
      if (typeof value === 'number') {
        return `$${value.toLocaleString()}`;
      }
    }
    
    if (lowerFieldName.includes('date') || lowerFieldName.includes('created') || lowerFieldName.includes('modified')) {
      if (typeof value === 'string' && (value.includes('T') || value.includes('Z'))) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      }
    }
    
    if (typeof value === 'object' && value !== null) {
      if (value.name) return value.name;
      if (value.title) return value.title;
      if (value.displayName) return value.displayName;
      if (value.email) return value.email;
      return JSON.stringify(value);
    }
    
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    
    return String(value);
  };

  const filteredItems = useMemo(() => {
    if (isDocumentLibraries || useBackendSearch || !localSearchTerm) return items;
    
    return items.filter(item => {
      const searchLower = localSearchTerm.toLowerCase();
      
      const topLevelMatch = Object.values(item).some(value => {
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchLower);
        }
        if (typeof value === 'object' && value !== null) {
          return JSON.stringify(value).toLowerCase().includes(searchLower);
        }
        return false;
      });
      
      let nestedMatch = false;
      if (item.fields && typeof item.fields === 'object') {
        nestedMatch = Object.values(item.fields).some(value => {
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchLower);
          }
          if (typeof value === 'object' && value !== null) {
            return JSON.stringify(value).toLowerCase().includes(searchLower);
          }
          return false;
        });
      }
      
      return topLevelMatch || nestedMatch;
    });
  }, [items, localSearchTerm, isDocumentLibraries, useBackendSearch]);

  const getFieldDisplayName = (fieldName: string): string => {
    const fieldMappings: { [key: string]: string } = {
      'id': 'ID',
      'createdBy': 'Created By',
      'modifiedBy': 'Modified By',
      'displayName': 'Display Name',
      'webUrl': 'Web URL',
      'serverUrl': 'Server URL',
      'listItemId': 'List Item ID',
      'projectid': 'Project ID',
      'projectname': 'Project Name',
      'projectdescription': 'Project Description',
      'startdate': 'Start Date',
      'enddate': 'End Date',
      'created_by': 'Created By',
      'web_url': 'Web URL'
    };

    const lowerFieldName = fieldName.toLowerCase();
    if (fieldMappings[lowerFieldName]) {
      return fieldMappings[lowerFieldName];
    }

    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    
    if (useBackendSearch && onSearch) {
      onSearch(searchValue);
    } else {
      setLocalSearchTerm(searchValue);
    }
  };

  const handleNextClick = async () => {
    if (isLoadingPage || !pagination.hasNextPage) return;
    
    try {
      await onNavigateNext();
    } catch (error) {
      console.error('Failed to navigate next:', error);
    }
  };

  const handlePreviousClick = async () => {
    if (isLoadingPage || !pagination.hasPrevPage) return;
    
    try {
      await onNavigatePrevious();
    } catch (error) {
      console.error('Failed to navigate previous:', error);
    }
  };

  const handlePageSizeChangeInternal = async (newPageSize: number) => {
    if (isLoadingPage || newPageSize === pagination.pageSize) return;
    
    try {
      await onPageSizeChange(newPageSize);
    } catch (error) {
      console.error('Failed to change page size:', error);
    }
  };

  const handleCheckboxChange = (itemId: string, checked: boolean, sharepointId: number) => {
    console.log('Legacy checkbox changed:', itemId, checked, sharepointId);
  };

  useEffect(() => {
    if (useBackendSearch) {
      setLocalSearchTerm("");
    }
  }, [useBackendSearch]);

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 p-2 border-b bg-white flex-shrink-0 text-sm">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to {itemType === 'lists' ? 'Lists' : 'Document Libraries'}
          </button>
          <span className="text-gray-400">•</span>
          <h2 className="font-semibold">
            {selectedItem?.title || selectedItem?.name || 'Loading...'}
          </h2>
        </div>

        <div className="flex-1 flex justify-center items-center">
          <div className="dotted-circle">
            {[...Array(10)].map((_, i) => (
              <span key={i} className="dot" style={{ "--i": i } as React.CSSProperties}></span>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 p-2 border-b bg-white flex-shrink-0 text-sm">
          <button
            onClick={onBack}
            className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap"
          >
            <ArrowLeft className="h-3 w-3" />
            Back to {itemType === 'lists' ? 'Lists' : 'Document Libraries'}
          </button>
          <span className="text-gray-400">•</span>
          <h2 className="font-semibold">
            {selectedItem?.title || selectedItem?.name || 'Error'}
          </h2>
        </div>

        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <div className="text-red-600 text-xl font-semibold mb-2">Error Loading Items</div>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center gap-2 p-2 border-b bg-white flex-shrink-0 text-sm">
        <button 
          onClick={onBack} 
          className="flex items-center gap-1 px-2 py-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors whitespace-nowrap"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to {itemType === 'lists' ? 'Lists' : 'Document Libraries'}
        </button>
        <span className="text-gray-400">•</span>
        <h2 className="font-semibold whitespace-nowrap">
          {selectedItem?.title || selectedItem?.name || 'Items'}
        </h2>
        <span className="text-gray-400">•</span>
        
        <div className="flex-1 relative min-w-0 mx-2">
        </div>
        
        {!isDocumentLibraries && (
          <>
            <span className="text-xs text-gray-600 whitespace-nowrap">Show:</span>
            <select 
              value={pagination.pageSize} 
              onChange={(e) => handlePageSizeChangeInternal(Number(e.target.value))} 
              className="border border-gray-300 rounded px-1 py-1 text-xs" 
              disabled={isLoadingPage}
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </>
        )}
        
        <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
          {isDocumentLibraries ? 
            `${filteredItems.length} items` : 
            `${pagination.itemCount} items (${pagination.pageSize} per page)`
          }
        </span>
      </div>

      {appliedFilters && Object.keys(appliedFilters).length > 0 && (
        <div className="p-2 border-b bg-gray-50 flex-shrink-0">
          <div className="flex flex-wrap gap-2">
            {Object.entries(appliedFilters).map(([key, value]) => (
              <span 
                key={key}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
              >
                {key}: {String(value)}
              </span>
            ))}
          </div>
        </div>
      )}

      {isDocumentLibraries  && (onDocumentExclusion || onLibraryExclusion)  && selectedItem && (
        <div className="p-3 bg-gray-50 border-b flex-shrink-0">
          <div className="flex items-center gap-3">
            <div
              className={`h-6 w-6 border-2 border-gray-300 rounded-full flex items-center justify-center cursor-pointer ${
                getLibrarySelectionState() === 'checked'
                  ? 'bg-black border-black'
                  : getLibrarySelectionState() === 'indeterminate'
                    ? 'bg-gray-400 border-gray-400'
                    : 'bg-white'
              }`}
              onClick={handleLibraryHeaderCheckboxClick}
            >
              {getLibrarySelectionState() === 'checked' && <Check className="h-4 w-4 text-white" />}
              {getLibrarySelectionState() === 'indeterminate' && <Minus className="h-4 w-4 text-white" />}
            </div>
            
            <div className="flex-1">
              <div className="font-medium text-gray-900">
                {selectedItem.title || selectedItem.name || 'Document Library'}
              </div>
              <div className="text-sm text-gray-600">
                {filteredItems.length} items • Click to select/deselect all documents
              </div>
            </div>

            <div className="text-sm text-gray-500">
              {getLibrarySelectionState() === 'checked' && 'All selected'}
              {getLibrarySelectionState() === 'unchecked' && 'None selected'}
              {getLibrarySelectionState() === 'indeterminate' && 'Partially selected'}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        {isLoadingPage && (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-600" />
            <span className="ml-2 text-gray-600">Loading...</span>
          </div>
        )}
        
        {!isLoadingPage && filteredItems.length === 0 ? (
          <div className="text-center py-8">
            <h3 className="text-xl font-semibold mb-2">No items found</h3>
            <p className="text-gray-600">
              {localSearchTerm || (appliedFilters && Object.keys(appliedFilters).length > 0) ? 
                'Try adjusting your search terms or filters.' : 
                `This ${isDocumentLibraries ? 'document library' : 'list'} may be empty or you may not have permission to view them.`
              }
            </p>
          </div>
        ) : !isLoadingPage ? (
          <div className="p-4">
            {isDocumentLibraries ? (
              <TreeView
                items={
                  filteredItems.length > 0 && filteredItems[0].name === "root" && filteredItems[0].children
                    ? filteredItems[0].children as SharePointListItem[]
                    : filteredItems
                }
                checkedItems={{}}
                onCheckboxChange={handleCheckboxChange}
                sharepointId={sharepointId}
  onDocumentExclusion={onDocumentExclusion}
  isDocumentExcluded={isDocumentExcluded}
  libraryId={selectedItem?.library_id || selectedItem?.drive_id || selectedItem?.id}
  sharepoint={sharepoint}
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border border-gray-300">
                    <thead>
                      <tr className="bg-gray-100">
                        {availableFields.map(field => (
                          <th key={field} className="border text-left p-2 text-sm font-medium">
                            {getFieldDisplayName(field)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map((item, index) => (
                        <tr key={item.id || `item-${index}`} className="border hover:bg-gray-50">
                          {availableFields.map(field => (
                            <td key={field} className="border p-2 text-sm max-w-[200px] truncate">
                              {formatFieldValue(getFieldValue(item, field), field)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        ) : null}
      </div>

      {!isDocumentLibraries && pagination.itemCount > 0 && (
        <div className="border-t bg-gray-50 p-2 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-600">
              Showing {pagination.itemCount} items ({pagination.pageSize} per page)
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousClick}
                disabled={!pagination.hasPrevPage || isLoadingPage}
                className="flex items-center gap-1 px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-xs"
                title={pagination.hasPrevPage ? "Go to previous page" : "No previous page available"}
              >
                <ChevronLeft className="h-3 w-3" />
                Previous
              </button>

              <div className="flex items-center gap-2 px-3 py-1 text-xs text-gray-600">
                <span className="flex items-center gap-1">
                  {pagination.hasPrevPage && (
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  )}
                  <span className="w-2 h-2 bg-gray-900 rounded-full"></span>
                  {pagination.hasNextPage && (
                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  )}
                </span>
                <span>
                  {!pagination.hasPrevPage && !pagination.hasNextPage 
                    ? "Single page"
                    : `Page ${pagination.hasPrevPage ? "..." : "1"}`
                  }
                </span>
              </div>

              <button
                onClick={handleNextClick}
                disabled={!pagination.hasNextPage || isLoadingPage}
                className="flex items-center gap-1 px-3 py-1 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors text-xs"
                title={pagination.hasNextPage ? "Go to next page" : "No more pages available"}
              >
                Next
                <ChevronRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharePointItemsView;